
import React, { useState } from 'react';
import { Prayer } from '@/types/prayer';
import { StandardDialogTemplate } from '@/components/shared/StandardDialogTemplate';
import CommentList from './CommentList';
import { LAYERS } from '@/utils/layers';
import { cn } from '@/lib/utils';

interface CommentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  prayer: Prayer;
  currentUserId?: string;
}


export default function CommentsDialog({
  isOpen,
  onClose,
  prayer,
  currentUserId
}: CommentsDialogProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const gestureCallbacks = {
    onExpand: () => setIsExpanded(true),
    onCollapseOrClose: () => {
      if (isExpanded) {
        setIsExpanded(false);
      } else {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <StandardDialogTemplate
      isOpen={isOpen}
      onClose={onClose}
      isExpanded={isExpanded}
      onToggleExpanded={handleToggleExpanded}
      gesturePreset="expand-collapse"
      gestureCallbacks={gestureCallbacks}
      customZIndex={LAYERS.SECONDARY_DIALOGS}
      excludeHeaderFooter={false}
      prayer={prayer}
      headerConfig={{
        variant: "regular",
        showHints: false,
        showToggleButton: true,
        showCloseButton: true
      }}
      backdropConfig={{
        enabled: true,
        clickToClose: true
      }}
      ariaLabel="Comments dialog"
    >
      <div className="flex flex-col h-full">
        {/* Prayer Information Header */}
        <div className="px-4 py-3 border-b border-border/50 bg-muted/30">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground line-clamp-1">
                {prayer.title || 'Prayer Request'}
              </h3>
              <span className="text-xs text-muted-foreground">
                by {prayer.author || 'Anonymous'}
              </span>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {prayer.content}
            </p>
          </div>
        </div>

        {/* Comments Section */}
        <div className={cn(
          "flex-1 overflow-hidden transition-all duration-400",
          // Add visual cue for expand/collapse state  
          isExpanded ? "opacity-100" : "opacity-95"
        )}>
          <CommentList
            prayerId={prayer.id}
            currentUserId={currentUserId}
            showForm={true}
          />
        </div>
      </div>
    </StandardDialogTemplate>
  );
}
